import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { map, Observable, firstValueFrom } from 'rxjs';

import { Banks } from 'src/app/domain/constants/bank.constant';
import { CountryValidationRules } from 'src/app/domain/constants/country-validation.constant';
import { Moedas } from 'src/app/domain/constants/moeda.constant';
import {
  ContactAccountDto,
  EAccountBankType,
} from 'src/app/domain/models/dto/contact-account.dto';
import { CountryDto } from 'src/app/domain/models/dto/country.dto';
import {
  Account,
  CreateOrderDto,
  Receiver,
} from 'src/app/domain/models/dto/create-order.dto';
import { CurrencyDto } from 'src/app/domain/models/dto/currency.dto';
import { FinalitiesDto } from 'src/app/domain/models/dto/finalities.dto';
import { ResponseObjectModel } from 'src/app/domain/models/response-object.model';
import { AuthenticationService } from 'src/app/shared/services';
import { BankAccountService } from 'src/app/shared/services/bank-account.service';
import { CountriesService } from 'src/app/shared/services/countries.service';
import {
  RequiredValidator,
  noWhitespaceValidator,
} from 'src/app/shared/validators/form-validators';

@Component({
  selector: 'app-beneficiary-selection',
  templateUrl: './beneficiary-selection.component.html',
  styleUrls: ['./beneficiary-selection.component.scss'],
})
export class BeneficiarySelectionComponent implements OnInit {
  @Input() data?: CreateOrderDto;
  @Input() loading?: boolean;
  @Input() warrenGlobalOnly?: boolean;
  @Input() country?: CountryDto;
  @Input() finality?: FinalitiesDto;
  @Input() okText = 'Finalizar';
  @Output() goBack = new EventEmitter<any>();
  @Output() closeModal = new EventEmitter<any>();
  @Output() beneficiarySave = new EventEmitter<CreateOrderDto>();

  form!: FormGroup;
  intermediaryBankForm!: FormGroup;
  currency: CurrencyDto;
  countryValidationRules = CountryValidationRules;

  selectedCurrency = false;
  countries$: Observable<CountryDto[]>;
  moedas = Moedas;
  showIntermediary = false;
  banks = Banks;
  isMobile = false;
  userContacts: ContactAccountDto[] = [];
  contactType: string = 'ReceiverAccount';
  isNewBeneficiary = false;

  private baseRequiredFieldsMainForm: string[] = [
    'senderReceiverName',
    'email',
    'country',
    'currency',
    'name',
    'amount',
    'swift',
  ];

  private readonly baseRequiredFieldsIntermediary: string[] = [
    'country',
    'city',
    'name',
    'swift',
  ];

  constructor(
    private fb: FormBuilder,
    private readonly countriesService: CountriesService,
    private readonly accountService: AuthenticationService,
    private readonly bankAccountService: BankAccountService
  ) {
    this.countries$ = this.countriesService.get().pipe(map((res) => res.data));
  }

  get companyId() {
    return +this.accountService.getCompanyId();
  }

  get individualId() {
    return +this.accountService.getIndividualId();
  }

  get isFormVisible(): boolean {
    return (
      this.isNewBeneficiary || !!this.form.get('senderReceiverName')?.value
    );
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 820;
    this.initializeForm();
    this.initializeIntermediaryForm();
    this.loadUserContacts();

    if (
      (this.finality?.id === 22 || this.finality?.id === 30) &&
      !this.baseRequiredFieldsMainForm.includes('fcc')
    ) {
      this.baseRequiredFieldsMainForm.push('fcc');
    }

    if (this.data) {
      this.populateFormWithExistingData();
    } else if (this.warrenGlobalOnly) {
      this.populateFormWithWarrenGlobalOnly();
    }
  }

  private populateFormWithWarrenGlobalOnly(): void {
    this.isNewBeneficiary = false;

    const contact: any = {
      country: {
        id: 13,
        description: 'Estados Unidos',
        code: '2496',
      },
      currency: {
        id: 1,
        description: 'USD',
        code: 220,
        ptaxes: [],
        beneficiaries: [],
        beneficiaryBankChannels: [],
        intermediaryBankChannels: [],
      },
      name: 'CITIBANK N.A.',
      swift: 'CITIUS33XXX',
      number: '40806826',
      aba: '021000089',
      bank: 'CITIBANK N.A.',
      isDefault: false,
      type: EAccountBankType.ReceiverAccount,
      senderReceiverName: 'Interactive Brokers LLC',
      email: 'warrencambio@warren.com.br',
    };

    this.form.patchValue({
      senderReceiverName: contact.senderReceiverName,
      email: contact.email,
      country: contact.country,
      currency: this.moedas.find((m) => m.id === contact.currency?.id),
      name: contact.name,
      swift: contact.swift,
      number: contact.number,
      aba: contact.aba,
      bank: contact.bank,
      fcc: null,
      isDefault: contact.isDefault,
      type: contact.type,
    });

    this.currency = contact.currency;
    this.selectedCurrency = true;

    this.updateFormValidators(this.form, contact.country);

    Object.keys(this.form.controls).forEach((key) => {
      if (key === 'amount' || key === 'fcc' || key === 'observation') {
        this.form.get(key)?.enable();
      } else {
        this.form.get(key)?.disable();
      }
    });
  }

  private populateFormWithExistingData(): void {
    if (!this.data?.receiver?.receiveraccount) return;

    this.isNewBeneficiary = false;

    this.countries$.subscribe((countries) => {
      const receiver = this.data!.receiver;
      const contact = receiver.receiveraccount;
      const intermediary = receiver.intermediaryaccount;

      const matchedCountry = countries.find((c) => c.id === receiver.countryId);
      const matchedCurrency = this.moedas.find(
        (m) => m.id === this.data!.currencyId
      );

      this.form.patchValue({
        senderReceiverName: receiver.name,
        email: contact.email,
        country: matchedCountry || '',
        currency: matchedCurrency || '',
        name: contact.name,
        swift: contact.swift,
        number: contact.number,
        aba: contact.aba,
        iban: contact.iban,
        blz: contact.blz,
        fcc: contact.fcc,
        cbu: contact.cbu,
        cuit: contact.cuit,
        bsb: contact.bsb,
        institutionNumber: contact.institutionNumber,
        transit: contact.transit,
        address: contact.address,
        cable: contact.cable,
        sortCode: contact.sortCode,
        type: contact.type || EAccountBankType.ReceiverAccount,
        isDefault: contact.isDefault,
        amount: this.data!.amount,
        observation: this.data!.observation,
      });

      this.currency = matchedCurrency || null;
      this.selectedCurrency = true;

      Object.keys(this.form.controls).forEach((key) => {
        if (key === 'amount' || key === 'observation') {
          this.form.controls[key].enable();
        } else {
          this.form.controls[key].disable();
        }
      });

      this.updateFormValidators(this.form, matchedCountry!);

      if (intermediary) {
        this.showIntermediary = true;

        const intermediaryCountry = countries.find(
          (c) => c.id === intermediary.countryId
        );

        this.intermediaryBankForm.patchValue({
          country: intermediaryCountry || '',
          city: intermediary.city,
          iban: intermediary.iban,
          bank: intermediary.bank,
          account: intermediary.account,
          swift: intermediary.swift,
          aba: intermediary.aba,
          name: intermediary.name,
          number: intermediary.number,
          blz: intermediary.blz,
          cbu: intermediary.cbu,
          cuit: intermediary.cuit,
          bsb: intermediary.bsb,
          institutionNumber: intermediary.institutionNumber,
          transit: intermediary.transit,
          address: intermediary.address,
          cable: intermediary.cable,
          sortCode: intermediary.sortCode,
        });
      }
    });
  }

  initializeForm(): void {
    this.form = this.fb.group({
      senderReceiverName: ['', [Validators.required, noWhitespaceValidator]],
      email: ['', [Validators.required, Validators.email]],
      country: ['', Validators.required],
      currency: [
        {
          id: 1,
          value: 'USD',
          description: 'USD',
          image: 'assets/flags/usd.svg',
          icon: 'usd',
        },
        Validators.required,
      ],
      name: ['', [Validators.required, noWhitespaceValidator]],
      swift: ['', [Validators.required, noWhitespaceValidator]],
      number: [''],
      aba: [''],
      iban: [''],
      blz: [''],
      observation: ['', Validators.maxLength(100)],
      cbu: [''],
      cuit: [''],
      bsb: [''],
      institutionNumber: [''],
      transit: [''],
      address: [''],
      fcc: [''],
      cable: [''],
      amount: ['', Validators.required],
      sortCode: [''],
      type: [EAccountBankType.ReceiverAccount],
      isDefault: [false],
    });

    this.form.get('country')?.valueChanges.subscribe(async (country) => {
      const selected = await this.getCountryById(country?.id);
      if (selected) this.updateFormValidators(this.form, selected);
    });

    this.form.get('currency')?.valueChanges.subscribe((currencyForm) => {
      this.currency = currencyForm;
      this.selectedCurrency = true;
    });
  }

  initializeIntermediaryForm(): void {
    this.intermediaryBankForm = this.fb.group({
      country: ['', [Validators.required, noWhitespaceValidator]],
      city: [''],
      iban: [''],
      bank: [''],
      account: [''],
      swift: [''],
      aba: [''],
      name: [''],
      number: [''],
      blz: [''],
      cbu: [''],
      cuit: [''],
      bsb: [''],
      institutionNumber: [''],
      transit: [''],
      address: [''],
      cable: [''],
      sortCode: [''],
    });

    this.intermediaryBankForm
      .get('country')
      ?.valueChanges.subscribe(async (country) => {
        const selected = await this.getCountryById(country?.id);
        if (!selected) return;

        const countryValue = this.intermediaryBankForm.get('country')?.value;

        Object.keys(this.intermediaryBankForm.controls).forEach((key) => {
          if (key === 'country') return;
          const control = this.intermediaryBankForm.get(key);
          control?.reset();
          control?.clearValidators();
          control?.updateValueAndValidity({ emitEvent: false });
        });

        this.intermediaryBankForm
          .get('country')
          ?.setValue(countryValue, { emitEvent: false });

        this.updateIntermediaryFormValidators(selected);
      });
  }

  compareCountry(obj1: CountryDto, obj2: CountryDto): boolean {
    return obj1 && obj2 ? obj1.id === obj2.id : obj1 === obj2;
  }

  onContactSelect(contact: ContactAccountDto) {
    this.isNewBeneficiary = false;
    this.form.reset();

    this.form.patchValue({
      senderReceiverName: contact.senderReceiverName,
      email: contact.email,
      country: contact.country,
      currency: this.moedas.find((m) => m.id === contact.currency?.id),
      name: contact.name,
      swift: contact.swift,
      number: contact.number,
      aba: contact.aba,
      iban: contact.iban,
      blz: contact.blz,
      cbu: contact.cbu,
      fcc: contact.fcc,
      cuit: contact.cuit,
      bsb: contact.bsb,
      institutionNumber: contact.institutionNumber,
      transit: contact.transit,
      address: contact.address,
      cable: contact.cable,
      sortCode: contact.sortCode,
      type: contact.type,
      isDefault: contact.isDefault,
    });

    this.currency =
      this.moedas.find((m) => m.id === contact.currency?.id) || null;
    this.selectedCurrency = true;

    const selectedCountry = contact.country;
    if (selectedCountry) this.updateFormValidators(this.form, selectedCountry);

    Object.keys(this.form.controls).forEach((key) => {
      if (key === 'amount' || key === 'observation') {
        this.form.controls[key].enable();
      } else {
        this.form.controls[key].disable();
      }
    });
  }

  onNewBeneficiary() {
    this.isNewBeneficiary = true;
    this.form.reset();

    Object.keys(this.form.controls).forEach((key) => {
      this.form.controls[key].enable();
    });

    this.form.controls['currency'].setValue(this.currency);
  }

  updateFormValidators(form: FormGroup, country: CountryDto): void {
    if (!country) return;

    const rules = this.countryValidationRules[country.code.toString()];
    if (!rules) return;

    Object.keys(form.controls).forEach((controlName) => {
      const control = form.get(controlName);
      if (control) {
        if (this.baseRequiredFieldsMainForm.includes(controlName)) {
          control.setValidators([Validators.required, noWhitespaceValidator]);
        } else if (rules.requiredFields.includes(controlName)) {
          control.setValidators([Validators.required, noWhitespaceValidator]);
        } else if (rules.optionalFields.includes(controlName)) {
          control.clearValidators();
        } else {
          control.clearValidators();
        }

        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  updateIntermediaryFormValidators(country: CountryDto): void {
    if (!country) return;

    const rules = this.countryValidationRules[country.code.toString()];
    if (!rules) return;

    const baseFields = this.baseRequiredFieldsIntermediary;

    Object.keys(this.intermediaryBankForm.controls).forEach((controlName) => {
      const control = this.intermediaryBankForm.get(controlName);
      if (!control) return;

      const isRequired =
        baseFields.includes(controlName) ||
        rules.requiredFields.includes(controlName);

      const isOptional = rules.optionalFields.includes(controlName);

      const shouldBeVisible = isRequired || isOptional;

      if (shouldBeVisible) {
        control.enable({ emitEvent: false });

        if (isRequired) {
          control.setValidators([Validators.required, noWhitespaceValidator]);
        } else {
          control.clearValidators();
        }
      } else {
        control.setValue(null);
        control.clearValidators();
        control.disable({ emitEvent: false });
      }

      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  shouldShowField(form: FormGroup, fieldName: string): boolean {
    const country = form.get('country')?.value;
    if (!country) return false;

    const rules = this.countryValidationRules[country.code.toString()];
    if (!rules) return false;

    const baseFields =
      form === this.intermediaryBankForm
        ? this.baseRequiredFieldsIntermediary
        : [...this.baseRequiredFieldsMainForm];

    if (baseFields.includes(fieldName)) return true;

    return (
      rules.requiredFields.includes(fieldName) ||
      rules.optionalFields.includes(fieldName)
    );
  }

  private fillFormWithContactAccount(contact: ContactAccountDto): void {
    this.form.patchValue({
      senderReceiverName: contact.senderReceiverName,
      email: contact.email,
      country: contact.country,
      currency: contact.currency,
      name: contact.name,
      swift: contact.swift,
      number: contact.number,
      aba: contact.aba,
      iban: contact.iban,
      blz: contact.blz,
      cbu: contact.cbu,
      cuit: contact.cuit,
      fcc: contact.fcc,
      bsb: contact.bsb,
      institutionNumber: contact.institutionNumber,
      transit: contact.transit,
      address: contact.address,
      cable: contact.cable,
      sortCode: contact.sortCode,
      type: contact.type,
      isDefault: contact.isDefault,
    });
  }

  createOrderDto(): CreateOrderDto {
    const formValue = this.form.getRawValue();
    const dto = new CreateOrderDto();

    dto.amount = formValue.amount;
    dto.currencyId = formValue.currency.id;
    dto.finalityId = this.finality?.id;
    dto.observation = formValue.observation;
    dto.currencydescription = formValue.currency.value;
    dto.receiver = new Receiver();
    dto.receiver.name = formValue.senderReceiverName;
    dto.receiver.countryId = formValue.country.id;
    dto.receiver.favorite = true;

    const rawAccount: ContactAccountDto = {
      email: formValue.email,
      countryId: formValue.country?.id,
      currency: formValue.currency,
      currencyId: formValue.currency?.id,
      name: formValue.name,
      swift: formValue.swift,
      number: formValue.number,
      aba: formValue.aba,
      iban: formValue.iban,
      blz: formValue.blz,
      cbu: formValue.cbu,
      fcc: formValue.fcc,
      cuit: formValue.cuit,
      bsb: formValue.bsb,
      institutionNumber: formValue.institutionNumber,
      transit: formValue.transit,
      address: formValue.address,
      cable: formValue.cable,
      sortCode: formValue.sortCode,
    };

    dto.receiver.receiveraccount = this.removeEmptyFields(rawAccount);

    if (this.companyId) dto.corporateAccountId = this.companyId;
    else if (this.individualId) dto.individualAccountId = this.individualId;

    return dto;
  }

  createIntermediaryBank(): ContactAccountDto {
    const value = this.intermediaryBankForm.getRawValue();

    const rawIntermediary: ContactAccountDto = {
      countryId: value.country?.id,
      city: value.city,
      name: value.name,
      swift: value.swift,
      number: value.number,
      account: value.account,
      iban: value.iban,
      bank: value.bank,
      aba: value.aba,
      blz: value.blz,
      cbu: value.cbu,
      cuit: value.cuit,
      bsb: value.bsb,
      institutionNumber: value.institutionNumber,
      transit: value.transit,
      address: value.address,
      cable: value.cable,
      sortCode: value.sortCode,
    };

    return this.removeEmptyFields(rawIntermediary);
  }

  removeEmptyFields<T>(obj: T): T {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as T);
  }

  submit() {
    if (this.isNewBeneficiary) {
      const formValue = this.form.getRawValue();

      const rawContact: ContactAccountDto = {
        senderReceiverName: formValue.senderReceiverName,
        email: formValue.email,
        country: formValue.country,
        currency: formValue.currency,
        name: formValue.name,
        swift: formValue.swift,
        number: formValue.number,
        aba: formValue.aba,
        iban: formValue.iban,
        fcc: formValue.fcc,
        blz: formValue.blz,
        cbu: formValue.cbu,
        cuit: formValue.cuit,
        bsb: formValue.bsb,
        institutionNumber: formValue.institutionNumber,
        transit: formValue.transit,
        address: formValue.address,
        cable: formValue.cable,
        sortCode: formValue.sortCode,
        isDefault: formValue.isDefault,
        type: EAccountBankType.ReceiverAccount,
      };

      const contactAccount = this.removeEmptyFields(rawContact);

      this.bankAccountService.addBankAccount(contactAccount).subscribe({
        next: (createdAccount: ResponseObjectModel<ContactAccountDto>) => {
          this.fillFormWithContactAccount(createdAccount.data);

          const updatedOrderDto: CreateOrderDto = this.createOrderDto();
          this.beneficiarySave.emit(updatedOrderDto);
        },
      });
    }

    const dto = this.createOrderDto();
    if (this.showIntermediary) {
      dto.receiver.intermediaryaccount = this.createIntermediaryBank();
    }
    this.beneficiarySave.emit(dto);
  }

  invalidSubmit(): boolean {
    return (
      this.form.invalid ||
      (this.showIntermediary && this.intermediaryBankForm.invalid)
    );
  }

  async getCountryById(id: number): Promise<CountryDto | undefined> {
    const countries = await firstValueFrom(this.countries$);
    const selected = countries.find((c) => c.id === id);
    return selected;
  }

  getCurrencyDescription(currency: any): string {
    const moeda = this.moedas.find((m) => m.id === currency.id);
    return moeda?.description || currency.description;
  }

  loadUserContacts(): void {
    this.bankAccountService
      .getAllSendersandReceivers(this.contactType, this.currency?.value)
      .subscribe({
        next: (res) =>
          (this.userContacts = res.data.sort((a, b) =>
            a.senderReceiverName?.localeCompare(b.senderReceiverName)
          )),
      });
  }

  close() {
    this.closeModal.emit(true);
  }

  back() {
    this.goBack.emit(true);
  }
}
